import { useCallback, useEffect, useMemo, useState } from "react";
import { Table, TableBody, TableContainer } from "@mui/material";
import TableHeading from "../DashboardTable/TableHeading/TableHeading";
import { createNotification } from "../../../utilities/notification";
import {
  localeStringOptions,
  MESSAGE_TYPE,
} from "../../../common/constants/common.constants";
import {
  getAmountOfProposalsForRealm,
  getApplications,
  getNumberOfNftsForRealm,
} from "../../../solana/common/helpers/user.dashboard.helpers";
import {
  IDashboardApplication,
  IMembersClubData,
  INftMemberAmountPerClub,
} from "../../../common/interfaces/user-dashboard.interface";
import "./ClubsTable.scss";
import DashboardTableCell from "../DashboardTable/DashboardTableCell/DashboardTableCell";
import { tableWrapper } from "../DashboardTable/TableHeading/TableHeading.styles";
import { UserRole } from "../../../common/enums/common.enums";
import { CLUB_DETAILS } from "../../../common/constants/routes.constants";
import TableTitleAndFilters from "../TableTitleAndFilters/TableTitleAndFilters";
import ClubTableCell from "../ClubTableCell/ClubTableCell";
import RoleTableCell from "../RoleTableCell/RoleTableCell";
import useModifier from "../../../hooks/useModifier";
import { DashboardFilter } from "../../../common/enums/dashboard.enums";
import DashboardTableRow from "../DashboardTable/DashboardTableRow/DashboardTableRow";
import {
  CLUB_TABLE_COLUMNS,
  MASTER_CLUB_TABLE_COLUMNS,
} from "../../../common/constants/dashboard.constants";
import { dashboardStore } from "../../../contexts/dashboardStore";
import clubsIcon from "../../../assets/clubs.svg";
import {
  getTreasurySymbol,
  getTrimmedPublicKey,
} from "../../../utilities/helpers";
import TableInfo from "../TableInfo/TableInfo";

const ClubsTable = () => {
  const { clubs, treasuryData, splProposals, setSplProposals, masterClubs } =
    dashboardStore();

  const [nftsAmount, setNftsAmount] = useState<INftMemberAmountPerClub[]>();
  const [applications, setApplications] = useState<IDashboardApplication[]>();
  const [cardDetails, setCardDetails] =
    useState<{ title: string; amount: string | number }[]>();

  const { addFilterByName, addFilterItem, filteredItems } = useModifier(
    clubs ?? [],
    DashboardFilter.Role
  );

  useEffect(() => {
    if (!splProposals) {
      void getSplProposals();
    }
    getAmountOfClubsWithRoles();

    void getNftsNumber();
    void getApplicationsData();
  }, [clubs]);

  const getNftsNumber = useCallback(async () => {
    try {
      if (!clubs) return;
      const nftsAmount = await getNumberOfNftsForRealm(clubs!);
      setNftsAmount(nftsAmount);
    } catch (error) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Problem loading number of nfts for club!"
      );
    }
  }, [clubs]);

  const getSplProposals = useCallback(async () => {
    try {
      setSplProposals(await getAmountOfProposalsForRealm(clubs!));
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Problem loading proposals data!");
    }
  }, [clubs]);

  const getApplicationsData = async () => {
    try {
      setApplications(await getApplications(clubs!));
    } catch (error) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Problem loading applications data"
      );
    }
  };

  const getNftAmountForClub = (clubAddress: string) => {
    const amount = nftsAmount?.find(
      (nftAmount) => nftAmount.clubAddress === clubAddress
    )?.nftMemberAmount;
    return amount;
  };

  const getNumberOfClubsBasedOnRole = useMemo(() => {
    return (role: UserRole) => {
      return clubs?.filter((c) => c.role === role).length;
    };
  }, [clubs]);

  const getNumberOfActiveProposals = useMemo(() => {
    return (clubData: IMembersClubData) => {
      return splProposals?.filter(
        (proposal) =>
          proposal.realmAddress.toString() === clubData.club.realmAddress
      ).length;
    };
  }, [splProposals]);

  const getApplicationsAmountForClub = useMemo(() => {
    return (clubData: IMembersClubData) => {
      return applications?.filter(
        (app) => app.clubAddress === clubData.club.clubAddress
      ).length;
    };
  }, [applications]);

  const getAmountOfClubsWithRoles = () => {
    const founderAmount = clubs!.filter(
      (club) => club.role === UserRole.owner
    ).length;
    const mangerAmount = clubs!.filter(
      (club) => club.role === UserRole.curator
    ).length;
    const memberAmount = clubs!.filter(
      (club) => club.role === UserRole.member
    ).length;
    const investorAmount = clubs!.filter(
      (club) => club.role === UserRole.investor
    ).length;
    setCardDetails([
      { amount: founderAmount, title: UserRole.owner },
      { amount: mangerAmount, title: UserRole.curator },
      { amount: memberAmount, title: UserRole.member },
      { amount: investorAmount, title: UserRole.investor },
    ]);
  };

  const renderClubs = useMemo(() => {
    const onlyMyClubs = filteredItems.filter((item) => !item.masterAddres);
    return onlyMyClubs.map((clubData, index) => {
      const treasury =
        treasuryData &&
        treasuryData.find(
          (treasury) => treasury?.club.clubAddress === clubData.club.clubAddress
        );
      return (
        <DashboardTableRow
          redirectUrl={`${CLUB_DETAILS}/${clubData.club.clubAddress}`}
          key={clubData.club.realmAddress}
        >
          <ClubTableCell
            clubImageUrl={clubData.club.clubImageUrl}
            clubName={clubData.club.clubName}
          />
          <RoleTableCell role={clubData.role} />
          <DashboardTableCell
            value={
              treasury !== null
                ? `${treasury?.balance.toLocaleString(
                    undefined,
                    localeStringOptions
                  )} ${getTreasurySymbol(treasury?.denominatedTokenInfo)}`
                : null
            }
          />
          <DashboardTableCell
            value={treasury !== null ? treasury?.profit : null}
          />
          <DashboardTableCell
            value={
              treasury !== null
                ? treasury?.ownership && treasury?.ownership + "%"
                : null
            }
            showPercentage
          />
          <DashboardTableCell value={clubData.club.numberOfMembers} />
          <DashboardTableCell
            value={getNftAmountForClub!(clubData.club.clubAddress!)}
          />
          <DashboardTableCell value={getNumberOfActiveProposals(clubData)} />
          <DashboardTableCell value={clubData.club.stakeCount} />
          <DashboardTableCell value={getApplicationsAmountForClub(clubData)} />
        </DashboardTableRow>
      );
    });
  }, [
    filteredItems,
    applications,
    treasuryData,
    splProposals,
    nftsAmount,
    getNumberOfActiveProposals,
    getApplicationsAmountForClub,
    addFilterByName,
    addFilterItem,
  ]);

  const renderMasterClubs = useMemo(() => {
    const masterFilteredClubs = filteredItems.filter(
      (item) => item.masterAddres
    );
    return masterFilteredClubs?.map((clubData, index) => {
      const treasury =
        treasuryData &&
        treasuryData.find(
          (treasury) => treasury?.club.clubAddress === clubData.club.clubAddress
        );
      return (
        <DashboardTableRow
          redirectUrl={`${CLUB_DETAILS}/${clubData.club.clubAddress}`}
          key={clubData.club.realmAddress}
        >
          <ClubTableCell
            clubImageUrl={clubData.club.clubImageUrl}
            clubName={clubData.club.clubName}
          />
          <RoleTableCell role={clubData.role} />
          <RoleTableCell role={getTrimmedPublicKey(clubData.masterAddres)} />

          <DashboardTableCell
            value={
              treasury !== null
                ? `${treasury?.balance.toLocaleString(
                    undefined,
                    localeStringOptions
                  )} ${getTreasurySymbol(treasury?.denominatedTokenInfo)}`
                : null
            }
          />
          <DashboardTableCell
            value={treasury !== null ? treasury?.profit : null}
          />
          <DashboardTableCell
            value={
              treasury !== null
                ? treasury?.ownership && treasury?.ownership + "%"
                : null
            }
            showPercentage
          />
          <DashboardTableCell value={clubData.club.numberOfMembers} />
          <DashboardTableCell
            value={getNftAmountForClub!(clubData.club.clubAddress!)}
          />
          <DashboardTableCell value={getNumberOfActiveProposals(clubData)} />
          <DashboardTableCell value={clubData.club.stakeCount} />
          <DashboardTableCell value={getApplicationsAmountForClub(clubData)} />
        </DashboardTableRow>
      );
    });
  }, [
    applications,
    treasuryData,
    splProposals,
    nftsAmount,
    getNumberOfActiveProposals,
    getApplicationsAmountForClub,
    addFilterByName,
    addFilterItem,
  ]);

  return (
    <div className="clubs-table">
      <TableInfo
        title="Member of"
        description={`${clubs?.length} Club`}
        icon={clubsIcon}
        cardDetails={cardDetails}
      />
      <TableTitleAndFilters
        tableTitle="List of your clubs"
        filtersData={[
          {
            filterTitle: "Founder",
            filterAmount: getNumberOfClubsBasedOnRole(UserRole.owner),
            onFilterSelect: () => addFilterItem(UserRole.owner),
          },
          {
            filterTitle: "Manager",
            filterAmount: getNumberOfClubsBasedOnRole(UserRole.curator),
            onFilterSelect: () => addFilterItem(UserRole.curator),
          },
          {
            filterTitle: "Member",
            filterAmount: getNumberOfClubsBasedOnRole(UserRole.member),
            onFilterSelect: () => addFilterItem(UserRole.member),
          },
          {
            filterTitle: "Investor",
            filterAmount: getNumberOfClubsBasedOnRole(UserRole.investor),
            onFilterSelect: () => addFilterItem(UserRole.investor),
          },
        ]}
      />

      <TableContainer style={tableWrapper}>
        <Table style={tableWrapper}>
          <TableHeading
            showInputField
            columns={CLUB_TABLE_COLUMNS}
            onInput={(e) => addFilterByName(e.target.value)}
          />
          <TableBody>{renderClubs}</TableBody>
        </Table>
      </TableContainer>
      {masterClubs && masterClubs.length > 0 && (
        <>
          <TableTitleAndFilters
            tableTitle="List of clubs from your master wallets"
            filtersData={[]}
          />
          <TableContainer style={tableWrapper}>
            <Table style={tableWrapper}>
              <TableHeading
                showInputField
                columns={MASTER_CLUB_TABLE_COLUMNS}
                onInput={(e) => addFilterByName(e.target.value)}
              />
              <TableBody>{renderMasterClubs}</TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </div>
  );
};

export default ClubsTable;
